import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../../components/PageContainer";
import { ActiveUsersCard } from "./ActiveUsersCard";
import { SelectBandCard } from "./SelectBandCard";
import { EngagementCard } from "./EngagementCard";
import { CityActiveUsersCard } from "./CityActiveUsersCard";
import { PlatformShareCard } from "./PlatformShareCard";
import { AppVersionCard } from "./AppVersionCard";
import { DateRangeNav } from "./DateRangeNav";
import { useDateRange } from "./useDateRange";

interface DashboardCardProps {
  title: string;
  subtitle?: string;
  onClick: () => void;
  children: React.ReactNode;
}

const DashboardCard = ({ title, subtitle, onClick, children }: DashboardCardProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Paper
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        p: 2,
        cursor: "pointer",
        border: "1px solid",
        borderColor: "divider",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1">{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
};

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const dateRange = useDateRange();
  const { startDate, endDate } = dateRange;

  return (
    <PageContainer title="Analytics Dashboard">
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <DateRangeNav dateRange={dateRange} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "repeat(12, 1fr)" },
        }}
      >
        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 8" } }}>
          <DashboardCard
            title="Active Users"
            subtitle="Trend"
            onClick={() => navigate("/analytics/active-users")}
          >
            <ActiveUsersCard startDate={startDate} endDate={endDate} />
          </DashboardCard>
        </Box>
        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
          <DashboardCard
            title="Engagement"
            subtitle="Avg time per active user"
            onClick={() => navigate("/analytics/engagement")}
          >
            <EngagementCard startDate={startDate} endDate={endDate} />
          </DashboardCard>
        </Box>
        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
          <DashboardCard
            title="Band Selections"
            subtitle="select_band events"
            onClick={() => navigate("/analytics/select-band")}
          >
            <SelectBandCard startDate={startDate} endDate={endDate} />
          </DashboardCard>
        </Box>
        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
          <DashboardCard
            title="Active Users by City"
            subtitle="Top locations"
            onClick={() => navigate("/analytics/cities")}
          >
            <CityActiveUsersCard limit={6} startDate={startDate} endDate={endDate} />
          </DashboardCard>
        </Box>
        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 4" } }}>
          <DashboardCard
            title="Active Users by Platform"
            subtitle="iOS vs Android"
            onClick={() => navigate("/analytics/platforms")}
          >
            <PlatformShareCard startDate={startDate} endDate={endDate} />
          </DashboardCard>
        </Box>
        <Box sx={{ gridColumn: { xs: "1 / -1", lg: "span 8" } }}>
          <DashboardCard
            title="Active Users by App Version"
            subtitle="Top versions"
            onClick={() => navigate("/analytics/app-versions")}
          >
            <AppVersionCard startDate={startDate} endDate={endDate} />
          </DashboardCard>
        </Box>
      </Box>
    </PageContainer>
  );
};

export default AnalyticsDashboard;
